/*
 * Copyright (c) 2026 Talent Catalog.
 *
 * This program is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or any later version.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License
 * for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see https://www.gnu.org/licenses/.
 */

import {Component, Input, OnInit} from '@angular/core';
import {TranslationService} from '../../../../services/translation.service';
import {User} from '../../../../model/user';
import {AuthorizationService} from "../../../../services/authorization.service";
import {LanguageService} from "../../../../services/language.service";
import {SystemLanguage} from "../../../../model/language";
import {CandidateOpportunityStage} from "../../../../model/candidate-opportunity";

/**
 * This admin settings component is used to configure translations for the candidate portal code.
 * <p/>
 * Candidate portal code supports multiple languages which the user can select.
 * In the actual code, wherever text is to be displayed its is done like this:
 * <pre>
 *       {{ 'TASKS.HEADER' | translate }}
 * </pre>
 * instead of actual text like "This is your tasks header".
 * <p/>
 * The "key" - TASKS.HEADER in the example above - is processed to be replaced by different text
 * based on the language that the user has selected.
 * <p/>
 * This component defines those keys - but not the values, which will always be null - in the
 * ALL_FIELDS structure below.
 * <p/>
 * The actual values are retrieved from Amazon's S3 store (there is one store for test and another
 * for production - so feel free to play around in your dev environment).
 * This Settings component allows a TC admin user to view the key values for different languages
 * and also to change the values.
 * <p/>
 * In the code, if there is no value assigned to a key, the text of the key itself is displayed.
 * The English translation is special. If there is no value assigned to a key for a particular
 * language, but there is a value assigned for the English language, then the English text is
 * displayed (better than just displaying the cryptic key).
 */
@Component({
  selector: 'app-general-translations',
  templateUrl: './general-translations.component.html',
  styleUrls: ['./general-translations.component.scss']
})
export class GeneralTranslationsComponent implements OnInit {

  @Input() loggedInUser: User;

  loading: boolean;
  languages: SystemLanguage[];
  systemLanguage: SystemLanguage;
  fields;
  fieldsFiltered;
  keys;

  saving: boolean;
  saveError: any;
  error: any;
  patchBusy: boolean;
  patchError: any;
  patchReview: any;
  patchAppliedSummary: any;
  pendingPatch: any;
  patchFileInput: HTMLInputElement;
  exportPrefixesText: string;
  exportKeysText: string;
  exportLanguages: string[];
  showExportForm: boolean;

  constructor(private translationService: TranslationService,
              private languageService: LanguageService,
              private authService: AuthorizationService) {
  }

  ngOnInit() {
    //Populate the translation keys for each candidate opp stage
    const CASE_STAGE_GROUP = "CASE-STAGE";
    ALL_FIELDS[CASE_STAGE_GROUP] = {};
    for (const key of Object.keys(CandidateOpportunityStage)) {
      ALL_FIELDS[CASE_STAGE_GROUP][key.toUpperCase()] = null;
    }

    this.loading = true;
    this.error = null;
    this.languageService.listSystemLanguages().subscribe(
      (result) => {
        this.languages = result;
        this.exportLanguages = this.languages.map(language => language.language);
        this.loading = false;
        this.setLanguage(this.languages[0]);
      },
      (error) => {
        this.error = error;
        this.loading = false;
      }
    )
    this.keys = Object.keys(ALL_FIELDS);
  }

  setLanguage(language: SystemLanguage) {
    this.error = null;
    this.loading = true;
    this.translationService.loadTranslationsFile(language.language).subscribe(
      (translations) => {
        this.loading = false;
        this.systemLanguage = language;
        this.fields = [];
        this.getFields(this.fields, null, ALL_FIELDS, translations);
        this.fieldsFiltered = this.fields;
      }, (error) => {
        this.loading = false;
        this.error = error;
      }
    );
  }

  getFields(results: {path, value}[], path, fieldsToFill, data) {
    Object.entries(fieldsToFill).map(([key, value]) => {
      let subPath = path ? `${path}.${key}` : key;
      if (value === null) {
        results.push({path: subPath.toLowerCase(), value: data ? data[key] : null});
      } else {
        this.getFields(results, subPath,  value, data ? data[key] : null);
      }
    });
  }

  save() {
    let result = {};
    this.fields.forEach(field => {
      let path = field.path.split('.');
      let target = result;
      for (let i = 0; i < path.length; i++) {
        const next = path[i].toUpperCase();
        if (i == path.length - 1) {
          target[next] = field.value;
        } else {
          target[next] = target[next] ? target[next] : {};
          target = target[next];
        }
      }
    });

    this.saving = true;
    this.saveError = null;
    this.translationService.updateTranslationFile(this.systemLanguage.language, result).subscribe(
      () => {
        this.saving = false;
      },
      error => {
        this.saving = false;
        this.saveError = error;
      }
    );
  }

  isBlank(value) {
    return (!value || /^\s*$/.test(value));
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  isAnAdmin(): boolean {
    return this.authService.isAnAdmin();
  }

  canManagePatch(): boolean {
    return this.authService.isSystemAdminOnly() && !this.loggedInUser?.readOnly;
  }

  filterItems($event) {
    if ($event != null) {
      this.fieldsFiltered = this.fields.filter(f => f.path.split('.')[0] === $event.toLowerCase())
    } else {
      this.fieldsFiltered = this.fields;
    }
  }

  triggerImport(fileInput: HTMLInputElement) {
    if (!this.canManagePatch() || this.patchBusy) {
      return;
    }
    this.patchAppliedSummary = null;
    fileInput.click();
  }

  onPatchFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.patchFileInput = input;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const patch = JSON.parse(String(reader.result));
        this.startPatchDryRun(patch);
      } catch (error) {
        this.patchError = error;
        this.patchReview = null;
        this.pendingPatch = null;
        this.clearPatchFileInput();
      }
    };
    reader.onerror = (error) => {
      this.patchError = error;
      this.patchReview = null;
      this.pendingPatch = null;
      this.clearPatchFileInput();
    };
    reader.readAsText(file);
  }

  startPatchDryRun(patch) {
    this.patchBusy = true;
    this.patchError = null;
    this.patchReview = null;
    this.pendingPatch = null;
    this.patchAppliedSummary = null;

    this.translationService.importPatch(patch, true, false).subscribe(
      (summary: any) => {
        this.patchBusy = false;
        this.pendingPatch = patch;
        this.patchReview = summary;
      },
      (error) => {
        this.patchBusy = false;
        this.patchError = error;
        this.clearPatchFileInput();
      }
    );
  }

  toggleExportForm() {
    this.patchError = null;
    this.patchAppliedSummary = null;
    this.showExportForm = !this.showExportForm;
    if (this.showExportForm && (!this.exportLanguages || this.exportLanguages.length === 0)) {
      this.exportLanguages = this.languages.map(language => language.language);
    }
  }

  exportPatch() {
    this.patchAppliedSummary = null;
    const prefixes = this.parseLines(this.exportPrefixesText);
    const keys = this.parseLines(this.exportKeysText);

    if (prefixes.length === 0 && keys.length === 0) {
      this.patchError = 'Specify at least one prefix or key before export.';
      return;
    }

    if (!this.exportLanguages || this.exportLanguages.length === 0) {
      this.patchError = 'Select at least one language before export.';
      return;
    }

    this.patchBusy = true;
    this.patchError = null;
    this.translationService.exportPatch({
      prefixes,
      keys,
      languages: this.exportLanguages
    }).subscribe(
      (patch) => {
        this.patchBusy = false;
        this.downloadPatchFile(patch);
      },
      (error) => {
        this.patchBusy = false;
        this.patchError = error;
      }
    );
  }

  confirmPatchImport() {
    if (!this.pendingPatch) {
      return;
    }
    this.patchBusy = true;
    this.patchError = null;
    this.translationService.importPatch(this.pendingPatch, false, false).subscribe(
      (appliedSummary: any) => {
        this.patchAppliedSummary = appliedSummary;
        this.patchReview = null;
        this.pendingPatch = null;
        this.patchBusy = false;
        this.clearPatchFileInput();
        if (this.systemLanguage) {
          this.setLanguage(this.systemLanguage);
        }
      },
      (error) => {
        this.patchBusy = false;
        this.patchError = error;
      }
    );
  }

  cancelPatchImport() {
    this.patchReview = null;
    this.pendingPatch = null;
    this.patchBusy = false;
    this.clearPatchFileInput();
  }

  getPatchLanguageRows(summary: any): any[] {
    if (!summary?.languages) {
      return [];
    }
    return Object.entries(summary.languages).map(([language, counts]: [string, any]) => ({
      language,
      totalKeys: counts?.totalKeys ?? 0,
      updatedKeys: counts?.updatedKeys ?? 0,
      unchangedKeys: counts?.unchangedKeys ?? 0,
    }));
  }

  hasPatchWarnings(summary: any): boolean {
    return !!summary?.warnings?.length;
  }

  private clearPatchFileInput() {
    if (this.patchFileInput) {
      this.patchFileInput.value = '';
    }
  }

  private parseLines(input: string): string[] {
    if (!input) {
      return [];
    }
    return Array.from(new Set(
      input
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0)
    ));
  }

  private downloadPatchFile(patch) {
    const output = JSON.stringify(patch, null, 2);
    const blob = new Blob([output], {type: 'application/json'});
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.download = `translation-patch-${timestamp}.json`;
    link.click();
    window.URL.revokeObjectURL(downloadUrl);
  }
}


const ALL_FIELDS = {
    "HEADER": {
      "NAV": {
        "ACCOUNT": null,
        "LOGOUT": null,
        "LOGIN": null,
        "PRIVACY": null,
        "PROFILE": null,
        "MY_AGREEMENTS": null,
        "UPLOAD": {
          "FILE": null,
          "PHOTO": null
        }
      },
      "LANG": {
        "SELECT": null,
      }
    },
    "JOI": {
      "BENEFITS": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "COST_COMMITMENT": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "EDUCATION_REQUIREMENTS": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "EMPLOYMENT_EXPERIENCE": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "LANGUAGE_REQUIREMENTS": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "LOCATION": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "LOCATION_DETAILS": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "MIN_SALARY": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "OCCUPATION_CODE": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "RECRUITMENT_PROCESS": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "SALARY_RANGE": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "SKILL_REQUIREMENTS": {
        "LABEL": null,
        "TOOLTIP": null
      },
      "VISA_PATHWAYS": {
        "LABEL": null,
        "TOOLTIP": null
      },
    },
    "STAFF_INSTRUCTION": {
       "CANDIDATE_NO_INTEREST": null,
       "CANDIDATE_UNSURE_INTEREST": null
     },
    "LOADING": null,
    "LOGIN": {
      "TITLE": null,
      "LABEL": {
        "USERNAME": null,
        "PASSWORD": null
      },
      "BUTTON": {
        "ALREADY": null,
        "FORGOT": null,
        "LOGIN": null
      }
    },
    "RESETPASSWORD": {
      "TITLE": null,
      "SUCCESS": null,
      "LABEL": {
        "EMAIL": null
      },
      "BUTTON": {
        "RESET": null
      }
    },
    "CHANGEPASSWORD": {
      "TITLE": null,
      "SUCCESS": null,
      "LABEL": {
        "OLDPASSWORD": null,
        "PASSWORD": null,
        "PASSWORDCONFIRMATION": null
      },
      "BUTTON": {
        "UPDATE": null
      }
    },
    "LANDING": {
      "TITLE": null,
      "REGISTER": null,
      "LOGIN": null,
      "USAFGHAN": {
        "HEADING1": null,
        "HEADING2": null
      }
    },
    "HOME": {
      "TITLE": null,
      "DRAFT": {
        "EXPLANATION": null,
        "BUTTON": null
      },
      "PENDING": {
        "EXPLANATION": null,
        "BUTTON": null
      },
      "ACTIVE": {
        "EXPLANATION": null,
        "BUTTON": null
      },
      "INCOMPLETE": {
        "EXPLANATION": null,
        "BUTTON": null
      },
      "EMPLOYED": {
        "EXPLANATION": null
      },
      "INACTIVE": {
        "EXPLANATION": null
      },
      "INELIGIBLE": {
        "EXPLANATION": null,
        "BUTTON": null
      },
      "EMAIL_VERIFICATION" : {
        "BUTTON": null,
        "IDLE": {
          "TITLE": null,
          "DESCRIPTION": null,
          "UPDATE_EMAIL": null,
          "SEND_BUTTON": null,
          "LINK": null
        },
        "LOADING": {
          "TITLE": null,
          "DESCRIPTION": null
        },
        "EMAIL_SENT" : {
          "TITLE": null,
          "DESCRIPTION": null,
          "SPAM": null,
          "BUTTON": null,
        },
        "ERROR":{
          "BUTTON":null,
        }
      }
    },
    "REGISTRATION": {
      "HEADER": {
        "ACCOUNT": {
          "EXPLANATION": null
        },
        "EXPLANATION": null,
        "STEP": null,
        "TITLE": {
          "ACCOUNT": null,
          "CONTACT": null,
          "CONTACT/ALTERNATE": null,
          "CONTACT/ADDITIONAL": null,
          "VERIFYPLUS": null,
          "PERSONAL": null,
          "OCCUPATION": null,
          "EXPERIENCE": null,
          "EDUCATION": null,
          "EDUCATION/MASTERS": null,
          "EDUCATION/UNIVERSITY": null,
          "EDUCATION/SCHOOL": null,
          "LANGUAGE": null,
          "EXAM": null,
          "CERTIFICATIONS": null,
          "DESTINATIONS": null,
          "ADDITIONAL": null,
          "UPLOAD": null,
          "SUBMIT": null
        }
      },
      "FOOTER": {
        "BACK": null,
        "CANCEL": null,
        "NEXT": null,
        "SUBMIT": null,
        "UPDATE": null
      },
      "VERIFY_PLUS": {
        "TITLE": null,
        "DESCRIPTION": null,
        "SUCCESS_TITLE": null,
        "SUCCESS_BODY": null,
        "DUPLICATE_BODY": null,
        "SKIP_HINT": null
      },
      "CONTACT": {
        "LABEL": {
          "EMAIL": null,
          "PHONE": null,
          "WHATSAPP": null,
          "RELOCATEDFIELDS": null,
          "RELOCATEDADDRESS": null,
          "RELOCATEDCITY": null,
          "RELOCATEDSTATE": null,
          "RELOCATEDCOUNTRY": null,
          "USERNAME": null,
          "PASSWORD": null,
          "PASSWORDCONFIRMATION": null,
          "CONTACTCONSENTREGISTRATION": null,
          "CONTACTCONSENTPARTNERS": null
        }
      },
      "PERSONAL": {
        "LABEL": {
          "FIRSTNAME": null,
          "LASTNAME": null,
          "GENDER": null,
          "DOB": null,
          "COUNTRYID": null,
          "CITY": null,
          "STATE": null,
          "YEAROFARRIVAL": null,
          "NATIONALITY": null,
          "OTHER_NATIONALITY": null,
          "OTHER_NATIONALITIES": null,
          "EXTERNALID": null,
          "REGISTEREDWITHUN": null,
          "REGISTRATIONID": null,
          "UNHCRCONSENT": null
        },
        "NOTE": {
          "STATELESS": null,
          "UNHCRREGISTERED": null,
          "UNHCRCONSENT": null
        }
      },
      "OCCUPATION": {
        "LABEL": {
          "HEADING": null,
          "ADD_SUBHEADING": null,
          "EDIT_HEADING": null,
          "NEW_HEADING": null,
          "SELECT_PRINCIPAL_HEADING": null,
          "SELECT_PRINCIPAL_SUBHEADING": null,
          "OCCUPATION": null,
          "YEARSEXPERIENCE": null,
          "DISCLAIMER": null,
          "MIGRATED_OCCUPATION": null,
          "PRINCIPAL_BADGE": null,
          "OCCUPATION_ADDED_CONFIRMATION": null,
          "PRINCIPAL_REMOVED_WARNING": null,
          "YEARS_OF_EXPERIENCE_SINGULAR": null,
          "YEARS_OF_EXPERIENCE_PLURAL": null
        },
        "BUTTON": {
          "ADD": null,
          "SAVE": null
        },
        "DELETE": {
          "TITLE": null,
          "CONFIRMATION": null,
          "YES": null,
          "NO": null
        }
      },
      "EXPERIENCE": {
        "BUTTON": {
          "ADD": null
        }
      },
      "EDUCATION": {
        "MESSAGE": null,
        "LABEL": {
          "MAXEDUCATIONLEVELID": null
        },
        "BUTTON": {
          "ADD": null
        }
      },
      "LANGUAGE": {
        "LABEL": {
          "LANGUAGE": null,
          "SPEAK": null,
          "WRITTEN": null
        },
        "BUTTON": {
          "ADD": null
        }
      },
      "EXAM": {
        "LABEL": {
          "EXAM": null,
          "SCORE": null,
          "YEAR": null,
          "NOTES": null,
          "OTHEREXAM":null
        },
        "BUTTON": {
          "ADD": null
        },
        "DELETE": {
          "TITLE": null,
          "CONFIRMATION": null,
          "YES": null,
          "NO": null
        }
      },
      "DESTINATIONS": {
        "LABEL": {
          "INTEREST": null,
          "NOTES": null,
        },
        "BUTTON": {
          "MARK_ALL_YES": null
        },
      },
      "CERTIFICATIONS": {
        "LABEL": {
          "NAME": null,
          "INSTITUTION": null,
          "DATECOMPLETED": null
        },
        "BUTTON": {
          "ADD": null
        }
      },
      "SUBMIT": {
        "INTRODUCTION": null,
        "CONSENT_INTRODUCTION": null,
        "LABEL": {
          "ADDITIONALINFO": null,
          "ASPIRATIONS": null,
          "SURVEY": null,
          "COMMENT": null,
        },
        "LINKEDIN": {
          "LABEL": null,
          "WARN": null
        },
        "NO_POLICY_ACCEPTANCE_TEXT": null,
        "POLICY_ACCEPTANCE_TEXT": null,
        "YOUR_PARTNER_IS": null,
      },
      "COMPLETE": {
        "TITLE": null,
        "PARA1": null,
        "BUTTON": {
          "LOGOUT": null,
          "PROFILE": null
        }
      },
      "ATTACHMENTS": {
        "TITLE": null,
        "EMPTYSTATE": null,
        "LABEL": {
          "NAME": null,
          "EDIT": null,
          "CREATEDBY": null,
          "CREATEDDATE": null
        },
        "CV": {
          "NAME": null,
          "EXPLANATION": null,
          "WARNING": null,
        },
        "OTHER": {
          "NAME": null,
          "EXPLANATION": null
        },
        "WARN": {
          "MOBILE": null
        }
      }
    },
    "PROFILE": {
      "TAB": {
        "PROFILE": null,
        "TASKS": null,
        "OPPS": null,
        "SERVICES": null,
        "AGREEMENTS": null,
      },
      "CONTACT": {
        "TITLE": null,
        "EMAIL": null,
        "PHONE": null,
        "WHATSAPP": null,
        "RELOCATEDADDRESS": null
      },
      "PERSONAL": {
        "TITLE": null,
        "FIRSTNAME": null,
        "LASTNAME": null,
        "GENDER": null,
        "DOB": null,
        "COUNTRY": null,
        "CITY": null,
        "STATE": null,
        "YEAROFARRIVAL": null,
        "NATIONALITY": null
      },
      "PRIVACY": {
        "CHANGED": null,
        "THANK_ACCEPTANCE": null,
        "VERSION": null,
        "ACCEPTED_ON": null,
        "ACCEPTED_WITH": null,
        "ACCEPTANCE_DETAILS": null
      },
      "OCCUPATIONS": {
        "TITLE": null
      },
      "EXPERIENCE": {
        "TITLE": null
      },
      "EDUCATION": {
        "TITLE": null
      },
      "EXAMS": {
        "TITLE": null
      },
      "CERTIFICATIONS": {
        "TITLE": null
      },
      "LANGUAGES": {
        "TITLE": null
      },
      "DESTINATIONS": {
        "TITLE": null
      },
      "OTHER": {
        "TITLE": null,
        "ADDITIONALINFO": null,
        "SURVEY": null,
        "SURVEYCOMMENT": null,
        "LINKEDIN": null,
      },
      "UPLOAD": {
        "TITLE": null
      },
      "BUTTON": {
        "EDIT": null,
        "CV": null
      }
    },
    "EDIT": {
      "TITLE": {
        "EDITING": null,
        "CONTACT": null,
        "OCCUPATIONS": null,
        "PERSONAL": null,
        "OCCUPATION": null,
        "EXPERIENCE": null,
        "EDUCATION": null,
        "LANGUAGES": null,
        "EXAMS": null,
        "CERTIFICATIONS": null,
        "DESTINATIONS": null,
        "ADDITIONAL": null,
        "UPLOAD": null
      }
    },
    "FORM": {
      "LABEL": {
        "OPTIONAL": null,
        "CLEAR": null,
        "CHOOSE": null,
        "SAVE": null,
        "APPROX": null
      },
      "PLACEHOLDER" : {
        "SELECT": null,
        "SELECTORTYPE": null,
        "SELECTOREXAM": null
      },
      "ERROR": {
        "REQUIRED": null,
        "EMAIL": null,
        "MINVALUE": null,
        "MINLENGTH": null,
        "DATE": null,
        "INVALIDDATERANGE": null
      },
      "JOBEXPERIENCE": {
        "LABEL": {
          "CANDIDATEOCCUPATIONID": null,
          "COMPANYNAME": null,
          "COUNTRY": null,
          "STARTDATE": null,
          "ENDDATE": null,
          "ROLE": null,
          "DESCRIPTION": null,
          "CONTRACTTYPE": {
            "TITLE": null,
            "FULLTIME": null,
            "PARTTIME": null
          },
          "EMPLOYMENTTYPE": {
            "TITLE": null,
            "PAID": null,
            "VOLUNTARY": null
          }
        },
        "BUTTON": {
          "ADD": null,
          "CANCEL": null,
          "SAVE": null
        }
      },
      "EDUCATION": {
        "EDUCATIONTYPE": null,
        "EDUCATIONMAJORID": null,
        "COURSENAME": null,
        "COUNTRYID": null,
        "INSTITUTION": null,
        "LENGTHOFCOURSEYEARS": null,
        "DATECOMPLETED": null,
        "INCOMPLETE": null
      },
      "ATTACHMENT": {
        "LABEL": {
          "DROP": null,
          "OR": null,
          "BROWSE": {
            "FILE": null,
            "IMAGE": null,
          },
          "UPLOADING": null,
          "PHOTO": null
        }
      }
    },
    "CARD": {
      "JOBEXPERIENCE": {
        "LABEL": {
          "FULLTIME": null,
          "PARTTIME": null,
          "PAID": null,
          "VOLUNTEER": null
        }
      },
      "EDUCATION": {
        "MAJOR": null
      }
    },
    "GENDER": {
      "MALE": null,
      "FEMALE": null,
      "OTHER": null
    },
    "EDUCATIONTYPE": {
      "ASSOCIATE": null,
      "VOCATIONAL": null,
      "BACHELOR": null,
      "MASTERS": null,
      "DOCTORAL": null
    },
    "ERROR": {
      "EMAIL_TAKEN": null,
      "PHONE_TAKEN": null,
      "WHATSAPP_TAKEN": null,
      "USER_DEACTIVATED": null,
      "INVALID_PASSWORD_MATCH": null,
      "PASSWORD_EXPIRED": null,
      "MISSING_OBJECT": null,
      "MISSING_WORK_EXPERIENCE": null,
      "INVALID_PASSWORD_TOKEN": null,
      "INVALID_PASSWORD_FORMAT": null,
      "FILE_DOWNLOAD_FAILED": null,
      "EXPIRED_PASSWORD_TOKEN": null,
      "ENTITY_REFERENCED": null,
      "UNKNOWN_OCCUPATION": null,
      "ALLOW_POPUPS": null,
      "CRITERIA_INVALID": {
        "HEADING": null,
        "LINK": null,
      },
    },
  "CONFIRMATION": {
    "YES": null,
    "NO": null,
    "UNSURE": null,
  },
  'SERVICES': {
    'TITLE': null,
    'DESCRIPTION': null,
    'DUOLINGO': {
      'TAG': null,
      'TITLE': null,
      'DESCRIPTION': null,
      'COUPONS_TITLE': null,
      'COUPONS_DESC': null,
      'COUPON_TITLE': null,
      'COUPON_CTA': null,
      'PAST_EXAMS': null,
      'SCORE': null,
      'YEAR': null,
      'EXAM': {
        'DEAR': null,
        'INTRO': null,
        'ABOUT': null,
        'ABOUT_TEXT': null,
        'TECHNICAL_STEPS': null,
        'TECHNICAL_STEPS_TEXT': null,
        'STEPS': null,
        'STEPS_TEXT': null,
        'GOOD_LUCK': null,
        'CTA_BUTTON': null,
      }
    },
    'LINKEDIN': {
      'TITLE': null,
      'DESCRIPTION': null,
      'NEW_ASSIGNMENT_INTRO': null,
      'NEW_ASSIGNMENT_VERIFY': null,
      'NEW_ASSIGNMENT_ENTER': null,
      'NEW_ASSIGNMENT_PLACEHOLDER': null,
      'LINKEDIN_URL_WARN': null,
      'VERIFY_BTN': null,
      'SUBMIT_BTN': null,
      'ASSIGNED': null,
      'REDEEM_BTN': null,
      'FAIL_TITLE': null,
      'FAIL_PARA': null,
      'BACK': null,
      'REDEEMED': {
        'TITLE': null,
        'DESCRIPTION': null,
        'ISSUE_TITLE': null,
        'ISSUE_DESCRIPTION': null,
        'ISSUE_DESCRIPTION_HERE': null,
        'REPORT_TITLE': null,
        'REPORT_BTN': null,
        'REPORT_THANKS': null,
        'REPORT_DESCRIPTION': null,
        'REPORT_COMMENT_PLACEHOLDER': null,
      }
    },
    'VERIFY_PLUS': {
      'TAG': null,
      'TITLE': null,
      'DESCRIPTION': null,
      'SCAN_INTRO': null,
      'SCANNER_ERROR': null,
      'REVIEW_TITLE': null,
      'SUBMIT_ERROR_FALLBACK': null,
      'SUBMIT_ERROR_HINT': null,
      'RESCAN': null,
      'CONFIRM': null,
      'CONSENT_LABEL': null,
      'SUCCESS_TITLE': null,
      'SUCCESS_BODY': null,
      'DUPLICATE_TITLE': null,
      'DUPLICATE_BODY': null,
      'DUPLICATE_RESCAN_HINT': null,
      'BACK': null,
      'SCANNER': {
        'ENABLE_CAMERA': null,
        'SCAN_AGAIN': null,
        'PERMISSION_DENIED': null,
        'TRY_AGAIN': null,
        'NO_CAMERA': null,
        'NOT_DETECTED': null
      }
    },
    'UNHCR': {
      'TAG': null,
      'TITLE': null,
      'DESCRIPTION': null,
      'DETAIL_TITLE': null,
      'DETAIL_SUBTITLE': null,
      'HELP_INTRO': null,
      'BACK': null
    },
    'REFERENCE': {
      'TAG': null,
      'TITLE': null,
      'DESCRIPTION': null,
      'DETAIL_TITLE': null,
      'DETAIL_DESCRIPTION': null,
      'OPC_DPA_ALERT': null,
      'ACKNOWLEDGE_OPC_DPA': null,
      'TERMS_ALERT': null,
      'ACCEPT_TERMS': null,
      'ASSIGN_INTRO': null,
      'ASSIGN_VOUCHER': null,
      'VOUCHER_CODE': null,
      'STATUS': null,
      'MARK_REDEEMED': null,
      'NOT_ELIGIBLE': null,
      'BACK': null
    }
  },
  "TASKS": {
    "TAB": null,
    "VIEWHELP": null,
    "ONGOING": {
      "HEADER": null,
      "NOTE": null,
    },
    "COMPLETED": {
      "HEADER": null,
      "NOTE": null,
    },
    "TABLE": {
      "NAME": null,
      "REQUIRED": null,
      "DUEDATE": null,
      "COMPLETED": null,
      "ABANDONED": null,
      "NONE": {
        "ACTIVETASKS": null,
        "COMPLETEDTASKS": null,
      }
    },
    "UPLOAD" : {
      "HEADER": null,
      "LOADING": null,
      "SUCCESS": null,
      "VIEW": null
    },
    "QUESTION": {
      "HEADER": null,
      "LABEL": null,
      "DROPDOWN": {
        "LABEL": null
      }
    },
    "SIMPLE": {
      "HEADER": null,
      "LABEL": null,
      "NOTE": null,
      "DOC": {
        "LABEL": null,
        "NOTE": null,
      },
    },
    "COMMENT": {
      "HEADER": null,
      "LABEL": null,
      "ABANDONED": {
        "LABEL": null,
        "NOTE": null,
      }
    },
    "TYPES": {
      "UPLOAD": null,
      "QUESTION": null,
    },
    "TASK": {
      "REQUIRED": null,
      "OPTIONAL": null,
      "DUEDATE": null,
      "ABANDONEDDATE": null,
      "COMPLETEDDATE": null,
      "OVERDUE": null,
      "VIEWHELP": null,
      "RETURN": null,
      "SUBMIT": null,
      "SUBMIT-COMMENT": null,
    }
  },
  "CANDIDATE-OPPS": {
    "HEADER": null,
    "NOTE": null,
    "TABLE": {
      "ALL-JOB-CANDIDATES-CHAT": null,
      "CHATS": null,
      "COMMENTS": null,
      "DESTINATION-CHAT": null,
      "NAME": null,
      "SOURCE-CHAT": null,
      "STAGE": null,
      "NONE": null,
      "OFFER": null,
      "INTERVIEW-GUIDANCE": null
    },
    "OPP" : {
      "CHAT-RETURN": null,
      "CHAT-MARK-AS-READ": null,
      "JOB-RETURN": null,
    },
  },
  "CHAT": {
      "HEADER": null,
      "POST-HEADER": null,
      "POST-SEND": null,
      "MARK-AS-READ": null,
      "NO-POSTS": null,
      "ADD-LINK": null,
      "LINK-PLACEHOLDER": null,
      "LINK-URL": null,
      "URL-PREFIX": null,
      "URL-PREFIX-ERROR": null,
      "LINK-EDIT-BTN": null,
      "LINK-REMOVE-BTN": null,
      "LINK-SAVE-BTN": null,
      "LINK-CANCEL-BTN": null,
      "NOTIFICATION_PREFERENCE": null,
      "NOTIFICATION_PREFERENCE_HELP": null
  },
  "CHAT_INFO": {
      "LABEL": {
        "PARTICIPANTS": null,
        "PURPOSE": null,
      },
      "HEADING": {
        "CANDIDATE_PROSPECT": null,
        "ALL_JOB_CANDIDATES": null,
        "JOB_CREATOR_SOURCE_PARTNER": null,
        "CANDIDATE_RECRUITING": null,
        "JOB_CREATOR_ALL_SOURCE_PARTNERS": null
      },
      "PARTICIPANTS": {
        "CANDIDATE_PROSPECT": null,
        "ALL_JOB_CANDIDATES": null,
        "JOB_CREATOR_SOURCE_PARTNER": null,
        "CANDIDATE_RECRUITING": null,
        "JOB_CREATOR_ALL_SOURCE_PARTNERS": null
      },
      "PURPOSE": {
        "CANDIDATE_PROSPECT": null,
        "ALL_JOB_CANDIDATES": null,
        "JOB_CREATOR_SOURCE_PARTNER": null,
        "CANDIDATE_RECRUITING": null,
        "JOB_CREATOR_ALL_SOURCE_PARTNERS": null
      }
  },
  "TRAVELINFO": {
    "MISMATCH": {
      "TITLE": null,
      "DESCRIPTION": null,
      "INSTRUCTION": null
    },
    "BUTTON": {
      "UPDATEPROFILE": null,
      "SUBMIT": null
    },
    "LABEL": {
      "BIRTHCOUNTRY": null,
      "PLACEOFBIRTH": null,
      "DOCTYPE": null,
      "DOCNUMBER": null,
      "DOCISSUEDBY": null,
      "ISSUEDATE": null,
      "EXPIRYDATE": null,
      "COMMENT": null
    },
    "OPTION": {
      "SELECTCOUNTRY": null,
      "PASSPORT": null,
      "NATIONALID": null,
      "REFUGEE": null
    }
  },
  "FAMILY-DOC":{
      "LABEL":{
        "NO_ELIGIBLE": null,
        "FAMILY-MEMBER": null,
        "RELATIONSHIP":null,
        "OTHER":null,
        "AGE-VALIDATION":null,
        "HEALTH-CONCERN":null,
        "REGISTERED":null,
        "REGISTRED-NUMBER":null,
        "DOC-VALIDATION":null,
        "ANOTHER-MEMBER":null
      }
  },
  "RSD-EVIDENCE":{
    "LABEL":{
      "COMMENT": null,
      "DOCTYPE": null,
      "DOCNUMBER": null,
      "REFUGEE-STATUS": null,
      "RSD-VALIDATION":null
    }
  },
  "FAMILY-RSD":{
      "LABEL":{
        "NO-MEMBER": null,
        "NOTHING-TO-DO": null
      }
  },
  "AGREEMENTS": {
    "NONE": null,
    "ACTIVE": null,
    "SUPERSEDED": null,
    "TERMS_VERSION": null,
    "ACCEPTED_DATE": null,
    "SUPERSEDED_DATE": null,
    "BACK": null,
    "COUNTERPARTY_TYPE": {
      "MANAGING_SOURCE_PARTNER": null,
      "DATABASE_PROVIDER": null,
      "SERVICE_PROVIDER": null
    },
    "LABEL": {
      "OPC_STANDARD_DATA_PROCESSING_AGREEMENT": null,
      "REFERENCE_SERVICE_TERMS": null,
      "GRN_CANDIDATE_PRIVACY_POLICY": null,
      "DEFAULT": null
    }
  }
}
